/**
 * usePreviewSourceFieldOptions — pola dodatkowe modułu (`system_module_fields`,
 * `GET /system-modules/:slug/fields`) kwalifikujące się jako źródło `field` panelu
 * podglądu dokumentu (patrz `preview-source.ts` i `DocumentPreviewViewElement.tsx`).
 *
 * Kwalifikacja: typ pola `file` | `image` | `gallery`, żywe (`!isCore &&
 * gcrecord === null`) — mirror filtru w `use-document-item-ext-fields.ts`.
 * `signature` CELOWO wykluczony — wartość pola signature jest base64 (obraz
 * podpisu wygenerowany inline), NIE kluczem/id do zasobu w `record_attachments`
 * czy MinIO, więc nie da się go pokazać przez `DocumentPreviewPanel` (który
 * oczekuje `RecordAttachmentDTO` ze streamowalną treścią pod `.../:id/content`).
 *
 * UWAGA — stan na dziś (2026-07-24): `SYSTEM_EXT_FIELD_TYPES`
 * (`packages/shared/src/field-types.ts`) — whitelist 12 typów dozwolonych dla
 * pól dodatkowych modułów SYSTEMOWYCH (ten mechanizm, `system_module_fields`) —
 * NIE zawiera `file`/`image`/`gallery`. Te 3 typy istnieją dziś WYŁĄCZNIE w
 * 48-typowym zestawie modułów WŁASNYCH (`CUSTOM_FIELD_TYPES`, tabele
 * `<code>_module_<slug>`) — inny mechanizm, inny zestaw tabel. Backendowy
 * endpoint `POST /system-modules/:slug/fields` odrzuci dziś próbę utworzenia
 * pola `file`/`image`/`gallery` na którymkolwiek z 9 hostów tego widgetu
 * (walidacja `SYSTEM_FIELD_TYPES.has(fieldType)` w route handlerze).
 *
 * Efekt: ten hook zwróci `options: []` dopóki backend nie rozszerzy tej
 * whitelisty o typy medialne — `propertiesEditor` renderuje wtedy pusty select
 * z komunikatem informacyjnym (zero crasha, zero fałszywych opcji). Mechanizm
 * jest przygotowany na przód: gdy whitelist się rozszerzy, zacznie zwracać
 * kandydatów bez żadnej zmiany w tym pliku. Zgłoszone jako finding do właściciela
 * repo — patrz podsumowanie taska.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api';

const ATTACHMENT_FIELD_TYPES: ReadonlySet<string> = new Set(['file', 'image', 'gallery']);

export interface PreviewSourceFieldOption {
  slug: string;
  name: string;
  fieldType: string;
}

interface RawSystemModuleField {
  slug: string;
  name: string;
  fieldType: string;
  isCore: boolean;
  gcrecord: string | null;
}

interface FieldsResponse {
  fields: RawSystemModuleField[];
}

export interface UsePreviewSourceFieldOptionsResult {
  /** Pola file/image/gallery żywe, w kolejności zwróconej przez API. Puste do czasu
   *  rozszerzenia whitelisty backendu — patrz nagłówek pliku. */
  options: PreviewSourceFieldOption[];
  isLoading: boolean;
}

/**
 * `moduleSlug` — `undefined` gdy kontekst (np. `useParams`) jeszcze nie rozwiązał
 * sluga; hook wtedy nie odpala zapytania (`enabled: false`) i zwraca `options: []`.
 */
export function usePreviewSourceFieldOptions(
  moduleSlug: string | undefined,
): UsePreviewSourceFieldOptionsResult {
  const query = useQuery({
    // Ten sam format klucza co `use-document-item-ext-fields.ts` — współdzielony
    // cache z każdym innym konsumentem `/system-modules/:slug/fields` tego modułu.
    queryKey: ['system-module-fields', moduleSlug],
    queryFn: async () => {
      const res = await api.get<FieldsResponse>(`/system-modules/${moduleSlug}/fields`);
      return res.data.fields;
    },
    enabled: !!moduleSlug,
    staleTime: 60_000,
  });

  const options = useMemo<PreviewSourceFieldOption[]>(
    () =>
      (query.data ?? [])
        .filter((f) => !f.isCore && f.gcrecord === null && ATTACHMENT_FIELD_TYPES.has(f.fieldType))
        .map((f) => ({ slug: f.slug, name: f.name, fieldType: f.fieldType })),
    [query.data],
  );

  return { options, isLoading: query.isLoading };
}
